import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, X } from "lucide-react";
import { COUNTRIES } from "@/data/countries";

/**
 * <CountrySelect /> — reusable searchable country picker.
 *
 * Two rendering modes:
 *   mode="country" (default) — full country name; value is the country name
 *   mode="dial"              — flag/code button showing the dial code; value is the dial code
 *
 * Search matches by country name OR dial code (e.g. "44", "+44", "united").
 */
export default function CountrySelect({
  mode = "country",
  value,
  onChange,
  placeholder = "Select country",
  testid = "country-select",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return COUNTRIES;
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(s) ||
        c.dial.toLowerCase().includes(s) ||
        c.dial.replace("+", "").includes(s.replace("+", "")) ||
        c.code.toLowerCase().includes(s)
    );
  }, [q]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Autofocus search when opened
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
    else setQ("");
  }, [open]);

  // Resolve label from value
  const selected = useMemo(() => {
    if (!value) return null;
    if (mode === "dial") return COUNTRIES.find((c) => c.dial === value);
    return COUNTRIES.find((c) => c.name === value);
  }, [value, mode]);

  const displayLabel = mode === "dial"
    ? (selected ? `${selected.code} ${selected.dial}` : placeholder)
    : (selected ? selected.name : placeholder);

  return (
    <div ref={rootRef} className={`relative ${className}`} data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={`${testid}-trigger`}
        className={`w-full flex items-center justify-between gap-2 bg-background border border-border rounded-xl px-4 py-2.5 text-sm text-left hover:border-primary/60 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 ${!selected ? "text-muted-foreground" : ""}`}
      >
        <span className="truncate">{displayLabel}</span>
        <ChevronDown className={`w-4 h-4 shrink-0 opacity-60 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute z-50 mt-2 w-full min-w-[280px] max-h-[320px] bg-background border border-border rounded-2xl shadow-[0_25px_70px_-15px_rgba(0,0,0,0.35)] overflow-hidden flex flex-col"
          data-testid={`${testid}-popover`}
        >
          <div className="relative border-b border-border">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search country or dial code…"
              className="w-full bg-transparent pl-11 pr-10 py-3 text-sm focus:outline-none"
              data-testid={`${testid}-search`}
            />
            {q && (
              <button
                type="button" onClick={() => setQ("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-foreground/10"
                aria-label="Clear"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto py-1" data-testid={`${testid}-list`}>
            {filtered.length === 0 && (
              <div className="px-4 py-6 text-center text-xs text-muted-foreground">No matches</div>
            )}
            {filtered.map((c) => {
              const isActive =
                mode === "dial" ? c.dial === value && c.code === selected?.code : c.name === value;
              return (
                <button
                  key={c.code}
                  type="button"
                  data-testid={`${testid}-item-${c.code}`}
                  onClick={() => {
                    onChange(mode === "dial" ? c.dial : c.name, c);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-foreground/10 transition-colors ${isActive ? "bg-primary/10" : ""}`}
                >
                  <span className="text-xs font-mono w-8 text-muted-foreground">{c.code}</span>
                  <span className="flex-1 text-left truncate">{c.name}</span>
                  <span className="text-xs font-mono text-primary">{c.dial}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
